import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Shield,
  Award,
  Clock,
  Users,
  CheckCircle2,
  ChevronRight,
  Droplets,
  Home,
  Flower2,
  Sun,
  BrickWall,
  Package,
  Settings,
  Wrench,
  Star,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSolutions, getSystems, getSettings, resolveMedia } from "@/lib/api";

const HERO_IMAGES = [
  "/assets/construtop/home/hero-1.webp",
  "/assets/construtop/home/hero-2.webp",
  "/assets/construtop/home/hero-3.webp",
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const stagger = {
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const iconMap = {
  clock: Clock,
  award: Award,
  users: Users,
  shield: Shield,
  check: CheckCircle2,
  home: Home,
  droplets: Droplets,
  "flower-2": Flower2,
  sun: Sun,
  "brick-wall": BrickWall,
  package: Package,
  settings: Settings,
  wrench: Wrench,
  star: Star,
  target: Target,
};

const DEFAULT_HOME_CONTENT = {
  hero: {
    eyebrow: "Expertos en Impermeabilización",
    title: "Soluciones que protegen",
    highlight: "tu obra",
    subtitle:
      "Más de 15 años resolviendo problemas de filtraciones, humedad e impermeabilización en el sector industrial, comercial y residencial del Ecuador.",
    primaryLabel: "VER SOLUCIONES",
    primaryHref: "/soluciones",
    secondaryLabel: "SOLICITAR ASESORÍA",
    secondaryHref: "/contacto",
    images: HERO_IMAGES,
  },
  stats: {
    items: [
      { icon: "clock", value: "15+", label: "Años de experiencia" },
      { icon: "award", value: "1M+", label: "m² impermeabilizados" },
      { icon: "users", value: "500+", label: "Proyectos realizados" },
      { icon: "shield", value: "Instalación", label: "Garantizada" },
    ],
  },
  solutionsPreview: {
    eyebrow: "Soluciones por tipo de obra",
    title: "Encuentra la solución para tu proyecto",
    description:
      "Cada obra tiene desafíos únicos. Identificamos tu problema y aplicamos el sistema adecuado.",
    ctaLabel: "VER TODAS LAS SOLUCIONES",
    ctaHref: "/soluciones",
    visibleItems: 5,
  },
  systemsPreview: {
    eyebrow: "Sistemas de impermeabilización",
    title: "Tecnología de clase mundial",
    description:
      "Trabajamos con las marcas más reconocidas de Italia para ofrecer sistemas de impermeabilización de la más alta calidad.",
    ctaLabel: "EXPLORAR SISTEMAS",
    ctaHref: "/sistemas",
  },
  trust: {
    eyebrow: "Por qué Construtop",
    title: "Confianza técnica y respaldo profesional",
    items: [
      {
        icon: "shield",
        title: "Garantía real",
        desc: "Instalación\nGarantizada",
      },
      {
        icon: "award",
        title: "Aliados internacionales",
        desc: "Socios exclusivos de General Membrane, MPM y Metic de Italia.",
      },
      {
        icon: "users",
        title: "Equipo certificado",
        desc: "Ingenieros debidamente capacitados.",
      },
      {
        icon: "check",
        title: "Diagnóstico integral",
        desc: "Levantamiento de especificaciones y diseño de soluciones a medida.",
      },
    ],
  },
  cta: {
    title: "¿Tiene un problema de impermeabilización?",
    description:
      "Nuestro equipo de ingenieros especializados le brindará una asesoría personalizada sin compromiso. Más de 500 proyectos exitosos nos respaldan.",
    backgroundImage: "/assets/construtop/home/hero-2.webp",
    primaryLabel: "SOLICITAR ASESORÍA GRATUITA",
    primaryHref: "/contacto",
    secondaryLabel: "ESCRIBIR POR WHATSAPP",
    secondaryHref: "https://wa.me/593999307185",
  },
};

function deepMerge(base, override) {
  if (!override || typeof override !== "object") return base;

  const result = { ...base };

  Object.keys(override).forEach((key) => {
    if (
      override[key] &&
      typeof override[key] === "object" &&
      !Array.isArray(override[key]) &&
      base[key] &&
      typeof base[key] === "object" &&
      !Array.isArray(base[key])
    ) {
      result[key] = deepMerge(base[key], override[key]);
    } else {
      result[key] = override[key];
    }
  });

  return result;
}

function renderMultiline(value) {
  return String(value || "")
    .split("\n")
    .map((line, index) => (
      <span key={index}>
        {line}
        {index < String(value || "").split("\n").length - 1 && <br />}
      </span>
    ));
}

function SmartButton({ href, children, variant = "default", className = "", ...props }) {
  const isExternal = /^https?:\/\//i.test(href || "") || String(href || "").startsWith("mailto:") || String(href || "").startsWith("tel:");

  if (!href) {
    return (
      <Button variant={variant} className={className} {...props}>
        {children}
      </Button>
    );
  }

  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        <Button variant={variant} className={className} {...props}>
          {children}
        </Button>
      </a>
    );
  }

  return (
    <Link to={href}>
      <Button variant={variant} className={className} {...props}>
        {children}
      </Button>
    </Link>
  );
}

function HeroSection({ content }) {
  const ref = useRef(null);
  const heroImages = (content?.images || []).filter(Boolean).length
    ? content.images.filter(Boolean)
    : HERO_IMAGES;

  const [currentImg, setCurrentImg] = useState(0);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    if (heroImages.length <= 1) return undefined;

    const timer = setInterval(() => {
      setCurrentImg((prev) => (prev + 1) % heroImages.length);
    }, 15000);

    return () => clearInterval(timer);
  }, [heroImages.length]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen min-h-[700px] flex items-center overflow-hidden"
    >
      <motion.div className="absolute inset-0 parallax-bg" style={{ y: bgY }}>
        <AnimatePresence mode="popLayout">
          <motion.img
            key={currentImg}
            src={resolveMedia(heroImages[currentImg] || heroImages[0])}
            alt={content?.title || "Construtop"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-[120%] object-cover"
          />
        </AnimatePresence>
      </motion.div>

      <div className="absolute inset-0 hero-overlay" />

      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 w-full"
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="max-w-3xl lg:ml-[9%]"
        >
          <motion.p
            variants={fadeUp}
            className="text-emerald-400 text-sm font-bold uppercase tracking-[0.25em] mb-6"
          >
            {content.eyebrow}
          </motion.p>

          <motion.h1
            variants={fadeUp}
            className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.05] mb-6"
          >
            {content.title}
            {content.highlight && (
              <span className="block text-emerald-400">{content.highlight}</span>
            )}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="text-zinc-300 text-base sm:text-lg max-w-xl leading-relaxed mb-10"
          >
            {content.subtitle}
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
            <SmartButton
              href={content.primaryHref}
              size="lg"
              data-testid="hero-primary-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-none px-8 h-14 text-base tracking-wide"
            >
              {content.primaryLabel} <ArrowRight className="ml-2" size={18} />
            </SmartButton>

            <SmartButton
              href={content.secondaryHref}
              size="lg"
              variant="outline"
              data-testid="hero-secondary-btn"
              className="border-white/30 text-white hover:bg-white/10 rounded-none px-8 h-14 text-base tracking-wide"
            >
              {content.secondaryLabel}
            </SmartButton>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[var(--construtop-surface)] to-transparent h-32"
      />
    </section>
  );
}

function StatsBar({ content }) {
  const stats = content?.items || DEFAULT_HOME_CONTENT.stats.items;

  return (
    <section
      data-testid="stats-bar"
      className="relative -mt-16 z-20 max-w-6xl mx-auto px-4 sm:px-8"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white border border-zinc-200 shadow-xl grid grid-cols-2 lg:grid-cols-4"
      >
        {stats.map((stat, i) => {
          const Icon = iconMap[stat.icon] || Shield;

          return (
            <div
              key={i}
              className={`p-6 lg:p-8 text-center ${
                i < stats.length - 1 ? "lg:border-r border-zinc-100" : ""
              }`}
            >
              <Icon className="mx-auto mb-3 text-emerald-600" size={28} />

              <div className="font-heading text-3xl lg:text-4xl text-zinc-900">
                {renderMultiline(stat.value)}
              </div>

              <div className="text-sm text-zinc-500 mt-1">
                {renderMultiline(stat.label)}
              </div>
            </div>
          );
        })}
      </motion.div>
    </section>
  );
}

function SolutionsPreview({ solutions, content }) {
  const limit = Math.max(Number(content?.visibleItems || 5), 5);

  const filteredSolutions = (solutions || []).filter(
    (sol) => (sol.slug || "").toLowerCase() !== "jardineras"
  );

  const visibleSolutions = filteredSolutions.slice(0, limit);

  return (
    <section data-testid="solutions-preview" className="section-spacing section-padding">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="mb-16"
        >
          <motion.p
            variants={fadeUp}
            className="text-emerald-600 text-sm font-bold uppercase tracking-[0.2em] mb-3"
          >
            {content.eyebrow}
          </motion.p>

          <motion.h2
            variants={fadeUp}
            className="font-heading text-3xl sm:text-4xl text-zinc-900 mb-4"
          >
            {content.title}
          </motion.h2>

          <motion.p variants={fadeUp} className="text-zinc-500 max-w-xl text-base">
            {content.description}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:auto-rows-[192px]">
          {visibleSolutions.map((sol, i) => {
            const Icon = iconMap[sol.icon] || Shield;

            return (
              <motion.div
                key={sol.id || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`group relative overflow-hidden bg-white border border-zinc-200 card-hover ${
                  i === 0
                    ? "md:col-span-2 lg:col-span-2 lg:row-span-2 h-80 lg:h-auto"
                    : "h-48"
                }`}
              >
                <div className="relative h-full overflow-hidden">
                  {sol.image_url ? (
                    <img
                      src={resolveMedia(sol.image_url)}
                      alt={sol.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-zinc-100 flex items-center justify-center">
                      <Icon className="text-zinc-300" size={42} />
                    </div>
                  )}

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-emerald-600 flex items-center justify-center">
                        <Icon size={20} className="text-white" />
                      </div>

                      <h3 className="font-heading text-xl text-white">{sol.title}</h3>
                    </div>

                    <p className="text-zinc-300 text-sm line-clamp-2 mb-3">
                      {sol.description}
                    </p>

                    <Link
                      to={`/soluciones?solution=${sol.slug}`}
                      data-testid={`home-solution-link-${sol.slug || i}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-white uppercase tracking-wide border border-white/50 px-3 py-1.5 hover:bg-white hover:text-zinc-900 transition-colors"
                    >
                      VER SOLUCIÓN <ChevronRight size={14} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {content.ctaLabel && (
          <div className="mt-10 text-center">
            <SmartButton
              href={content.ctaHref}
              variant="outline"
              data-testid="view-all-solutions-btn"
              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-none px-8 font-bold tracking-wide"
            >
              {content.ctaLabel} <ChevronRight className="ml-1" size={16} />
            </SmartButton>
          </div>
        )}
      </div>
    </section>
  );
}

function SystemsPreview({ systems, content }) {
  return (
    <section
      data-testid="systems-preview"
      className="section-spacing section-padding bg-zinc-950 relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-600/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="mb-16"
        >
          <motion.p
            variants={fadeUp}
            className="text-emerald-400 text-sm font-bold uppercase tracking-[0.2em] mb-3"
          >
            {content.eyebrow}
          </motion.p>

          <motion.h2
            variants={fadeUp}
            className="font-heading text-3xl sm:text-4xl text-white mb-4"
          >
            {content.title}
          </motion.h2>

          <motion.p variants={fadeUp} className="text-zinc-400 max-w-xl text-base">
            {content.description}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(systems || []).map((sys, i) => {
            const features = sys.features || sys.advantages || [];

            return (
              <motion.div
                key={sys.id || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="group bg-zinc-900 border border-zinc-800 hover:border-emerald-600/30 transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  {sys.image_url ? (
                    <img
                      src={resolveMedia(sys.image_url)}
                      alt={sys.title}
                      className="w-full h-full object-contain bg-zinc-900 transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                      <Shield className="text-zinc-600" size={40} />
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <h3 className="font-heading text-xl text-white mb-3">{sys.title}</h3>

                  <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3 mb-4">
                    {sys.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {features.slice(0, 3).map((feature, j) => (
                      <span
                        key={j}
                        className="text-xs px-2 py-1 bg-emerald-600/10 text-emerald-400 border border-emerald-600/20"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {content.ctaLabel && (
          <div className="mt-10 text-center">
            <SmartButton
              href={content.ctaHref}
              data-testid="view-all-systems-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none px-8 font-bold tracking-wide"
            >
              {content.ctaLabel} <ArrowRight className="ml-2" size={16} />
            </SmartButton>
          </div>
        )}
      </div>
    </section>
  );
}

function TrustSection({ content }) {
  const items = content?.items || [];

  return (
    <section data-testid="trust-section" className="section-spacing section-padding">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeUp}
            className="text-emerald-600 text-sm font-bold uppercase tracking-[0.2em] mb-3"
          >
            {content.eyebrow}
          </motion.p>

          <motion.h2
            variants={fadeUp}
            className="font-heading text-3xl sm:text-4xl text-zinc-900 mb-4"
          >
            {content.title}
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {items.map((item, i) => {
            const Icon = iconMap[item.icon] || Shield;

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-5 bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                  <Icon className="text-emerald-600" size={28} />
                </div>

                <h3 className="font-heading text-lg text-zinc-900 mb-2">
                  {item.title}
                </h3>

                <p className="text-zinc-500 text-sm leading-relaxed">
                  {renderMultiline(item.desc)}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CTASection({ content }) {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} data-testid="cta-section" className="relative py-32 overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        {content.backgroundImage ? (
          <img
            src={resolveMedia(content.backgroundImage)}
            alt=""
            className="w-full h-[130%] object-cover"
          />
        ) : (
          <div className="w-full h-full bg-emerald-950" />
        )}
      </motion.div>

      <div className="absolute inset-0 bg-emerald-900/85" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.h2
            variants={fadeUp}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6"
          >
            {content.title}
          </motion.h2>

          <motion.p
            variants={fadeUp}
            className="text-emerald-100/80 text-lg max-w-2xl mx-auto mb-10"
          >
            {content.description}
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
            <SmartButton
              href={content.primaryHref}
              size="lg"
              data-testid="cta-primary-btn"
              className="bg-white text-emerald-800 hover:bg-zinc-100 rounded-none px-8 h-14 text-base font-bold tracking-wide"
            >
              {content.primaryLabel}
            </SmartButton>

            <SmartButton
              href={content.secondaryHref}
              size="lg"
              variant="outline"
              data-testid="cta-secondary-btn"
              className="border-white/40 text-white hover:bg-white/10 rounded-none px-8 h-14 text-base tracking-wide"
            >
              {content.secondaryLabel}
            </SmartButton>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const [solutions, setSolutions] = useState([]);
  const [systems, setSystems] = useState([]);
  const [homeContent, setHomeContent] = useState(DEFAULT_HOME_CONTENT);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [solRes, sysRes, settingsRes] = await Promise.all([
          getSolutions(),
          getSystems(),
          getSettings(),
        ]);

        let nextContent = DEFAULT_HOME_CONTENT;

        try {
          const savedContent = JSON.parse(settingsRes.data?.home_content || "{}");
          nextContent = deepMerge(DEFAULT_HOME_CONTENT, savedContent);
        } catch {
          nextContent = DEFAULT_HOME_CONTENT;
        }

        try {
          const legacyHeroImages = JSON.parse(
            settingsRes.data?.home_hero_images || "[]"
          ).filter(Boolean);

          if (
            legacyHeroImages.length &&
            !settingsRes.data?.home_content
          ) {
            nextContent = {
              ...nextContent,
              hero: {
                ...nextContent.hero,
                images: legacyHeroImages,
              },
            };
          }
        } catch {}

        setHomeContent(nextContent);

        if ((solRes.data || []).length === 0 && (sysRes.data || []).length === 0) {
          const api = (await import("@/lib/api")).default;
          await api.post("/seed");

          const [solRes2, sysRes2] = await Promise.all([
            getSolutions(),
            getSystems(),
          ]);

          setSolutions(solRes2.data || []);
          setSystems(sysRes2.data || []);
        } else {
          setSolutions(solRes.data || []);
          setSystems(sysRes.data || []);
        }
      } catch {
        setHomeContent(DEFAULT_HOME_CONTENT);
      }
    };

    loadData();
  }, []);

  return (
    <main data-testid="home-page" className="bg-construtop-texture">
      <HeroSection content={homeContent.hero} />
      <StatsBar content={homeContent.stats} />
      <SolutionsPreview
        solutions={solutions}
        content={homeContent.solutionsPreview}
      />
      <SystemsPreview systems={systems} content={homeContent.systemsPreview} />
      <TrustSection content={homeContent.trust} />
      <CTASection content={homeContent.cta} />
    </main>
  );
}